import React, { useEffect, useState } from 'react';
import CourtAssignment from './CourtAssignment';
import SessionPlayerPill from './SessionPlayerPill';

/** True when the court grid is a single vertical column (same breakpoints as the grid classes). */
function getCourtsStackedVertically(courtLayout) {
  if (typeof window === 'undefined') return true;
  if (courtLayout === '1' || courtLayout === 'auto') return true;
  if (courtLayout === '2') return !window.matchMedia('(min-width: 1024px)').matches;
  return !window.matchMedia('(min-width: 640px)').matches;
}

function useCourtsStackedVertically(courtLayout) {
  const [stacked, setStacked] = useState(() => getCourtsStackedVertically(courtLayout));
  useEffect(() => {
    const update = () => setStacked(getCourtsStackedVertically(courtLayout));
    update();
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const query = courtLayout === '2' ? '(min-width: 1024px)' : '(min-width: 640px)';
    if (courtLayout === '1' || courtLayout === 'auto') return undefined;
    const mql = window.matchMedia(query);
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, [courtLayout]);
  return stacked;
}

// Next court uses shared SessionPlayerPill
function NextCourtPlayerPill({
  player,
  team,
  courtNumber,
  onRemove,
  currentClubId = 'default',
  teammate,
  useSinglesElo = false,
  waitMetricSlice = null,
}) {
  return (
    <SessionPlayerPill
      player={player}
      teammate={teammate}
      variant="nextCourt"
      onRemove={onRemove}
      team={team}
      courtNumber={courtNumber}
      removeTitle="Remove from next court"
      useSinglesElo={useSinglesElo}
      waitMetricSlice={waitMetricSlice}
    />
  );
}

const CourtsList = ({
  courtAssignments,
  waitingList,
  pausedList = [],
  numberOfCourts,
  numberOfWaitingCourts,
  adjustCourtCount,
  adjustWaitingCourtCount,
  updateMatchResult,
  rotatePlayersAfterMatch,
  removeCourt,
  generateWaitingCourts,
  onAddPlayerToCourt,
  onRemovePlayerFromCourt, // <-- Add missing prop
  setWaitingList,
  currentClubId,
  getMaxCourts,
  canRotateAllCourts,
  playersById = {},
  sessionPlayerStatus = {},
  onShowManageCourts, // <-- new prop
  onFillEmptyCourt, // <-- new prop
  onPromoteUpcomingMatches, // <-- new prop for promoting upcoming matches
  nextAssignments = [], // <-- new prop for upcoming matches
  isSocialSession = false, // <-- new prop for social sessions
  socialSessionActivePlayers = [], // <-- new prop for social session active players
  socialCourtPlayers = [], // <-- new prop for social court players
  courtLayout = '1', // <-- new prop for court layout selection (default to 1, always 1 on mobile)
  matchmakingValidation = { possible: true, reason: '' }, // <-- new prop for matchmaking validation
  playerStreaks = {}, // <-- new prop for player win streaks
  canEnterMatchScores = true, // host/admin only on competitive sessions
  showPostMatchRotateButton = true,
  /** When set (competitive), overrides built-in pick list for “playing / upcoming” pool */
  courtPickPlayingPlayers = null,
  courtPickWaitingManagementPlayers = null,
  playersPerSide = 2,
  gridDensity = 'comfortable',
  /** When set (competitive), court with this `courtNumber` gets a short green border after score + rotation */
  freshRotationHighlightCourtNumber = null,
  /** Per-player wait metrics (courts watched, cooldown, match preference) for tooltips */
  playerWaitMetrics = {},
  /** Whether the current viewer is the session host — shows court-number up/down */
  isHostView = false,
  /** Host court-number move: (courtNumber, direction) where direction is -1 or +1 */
  onMoveCourt,
  /** True while a court-number save is in flight — disables the arrows */
  courtReorderBusy = false,
  /** 'full' | 'mobilePlayer' — mobilePlayer renders compact side-by-side layout on mobile for player viewers */
  courtCardLayout = 'full',
  /** Viewer's player ID for "(You)" highlight in mobile compact layout */
  viewerPlayerId = null,
  partnerships = [],
  scoringConfig,
  scheduledGames = [],
  showEloTooltip = true,
  pegBoardQueue = null,
  pegBoardPickTarget = null,
  onOpenPegBoardPick,
}) => {
  const compactGrid = gridDensity === 'compact';
  const courtsStackedVertically = useCourtsStackedVertically(courtLayout);
  // Remove useEffect logs for mount and render
  // Defensive: Ensure playersById and sessionPlayerStatus are always objects
  if (!playersById || typeof playersById !== 'object') playersById = {};
  if (!sessionPlayerStatus || typeof sessionPlayerStatus !== 'object') sessionPlayerStatus = {};
  const filteredWaitingList = waitingList.filter(id => playersById[id] && sessionPlayerStatus[id] === 'active');

  let pickPlayingPlayers;
  let pickWaitingManagementPlayers;
  if (isSocialSession) {
    pickPlayingPlayers = socialSessionActivePlayers.filter((player) =>
      !socialCourtPlayers.some((cp) => cp.playerId === player.id)
    );
    pickWaitingManagementPlayers = [];
  } else if (courtPickPlayingPlayers != null) {
    pickPlayingPlayers = courtPickPlayingPlayers;
    pickWaitingManagementPlayers = courtPickWaitingManagementPlayers ?? [];
  } else {
    const waitingPlayers = filteredWaitingList.map((id) => playersById[id]).filter(Boolean);
    const upcomingPlayers = nextAssignments.flatMap((match) =>
      [...(match.team1 || []), ...(match.team2 || [])].map((id) => playersById[id]).filter(Boolean)
    );
    const allPlayerIds = new Set([
      ...waitingPlayers.map((p) => p.id),
      ...upcomingPlayers.map((p) => p.id),
    ]);
    pickPlayingPlayers = Array.from(allPlayerIds)
      .map((id) => playersById[id])
      .filter(Boolean);
    pickWaitingManagementPlayers = [];
  }
  // Remove filteredCourtAssignments for on-court assignments
  // Use the original courtAssignments when rendering CourtAssignment
  // For example, in renderNextCourt and renderTeam, use playersById[playerId] for display

  // Remove all nextCourts state, generateNextCourts, and related logic, as rotation is now handled by the rolling queue logic from the parent.

  // Check if any team on any court has a spot
  const hasCourtSpot = () => {
    return courtAssignments.some(
      (court) =>
        court.team1.length < playersPerSide || court.team2.length < playersPerSide
    );
  };

  // Check if any team on any next court has a spot
  const hasNextCourtSpot = () => {
    return false; // No more next courts to check
  };

  // Remove a player from court and add to waiting list
  const handleRemovePlayerFromCourt = (playerId, team, courtNumber) => {
    const updatedCourtAssignments = courtAssignments.map(court => {
      if (court.courtNumber === courtNumber) {
        const teamKey = team === 1 ? 'team1' : 'team2';
        return {
          ...court,
          [teamKey]: court[teamKey].filter(id => id !== playerId)
        };
      }
      return court;
    });
    // Only add to waiting list if not already present
    if (!waitingList.includes(playerId)) {
      onAddPlayerToCourt && onAddPlayerToCourt(playerId, true); // true = force add to waiting list
    }
    // Update court assignments
    if (typeof onAddPlayerToCourt === 'function') {
      // Let parent handle waiting list update
      onAddPlayerToCourt(null, false, updatedCourtAssignments);
    }
  };


  // Swap a player with another player on the same court
  const handleSwapPlayer = (currentPlayerId, newPlayerId, courtNumber, teamNumber) => {
    const updatedCourtAssignments = courtAssignments.map(court => {
      if (court.courtNumber === courtNumber) {
        const teamKey = teamNumber === 1 ? 'team1' : 'team2';
        const otherTeamKey = teamNumber === 1 ? 'team2' : 'team1';
        
        // Replace the current player with the new player in the same team
        const updatedTeam = court[teamKey].map(id => 
          id === currentPlayerId ? newPlayerId : id
        );
        
        return {
          ...court,
          [teamKey]: updatedTeam
        };
      }
      return court;
    });
    
    // Update court assignments
    if (typeof onAddPlayerToCourt === 'function') {
      onAddPlayerToCourt(null, false, updatedCourtAssignments);
    }
  };

  // Remove a player from next court
  const handleRemovePlayerFromNextCourt = (playerId, team, courtNumber) => {
    // Update next courts state
    // const updatedNextCourts = nextCourts.map(court => {
    //   if (court.courtNumber === courtNumber) {
    //     const teamKey = team === 1 ? 'team1' : 'team2';
    //     return {
    //       ...court,
    //       [teamKey]: court[teamKey].filter(p => p.id !== player.id)
    //     };
    //   }
    //   return court;
    // });
    
    // setNextCourts(updatedNextCourts);
  };

  // Add a player to a next court
  const handleAddPlayerToNextCourt = (player) => {
    // Find the first next court with an empty spot
    // const updatedNextCourts = [...nextCourts];
    // let added = false;
    
    // for (let court of updatedNextCourts) {
    //   if (court.team1.length < 2) {
    //     court.team1.push(player);
    //     added = true;
    //     break;
    //   } else if (court.team2.length < 2) {
    //     court.team2.push(player);
    //     added = true;
    //     break;
    //   }
    // }
    
    // // If no existing court has space, create a new one
    // if (!added && updatedNextCourts.length === 0) {
    //   updatedNextCourts.push({
    //     courtNumber: 1,
    //     team1: [player],
    //     team2: []
    //   });
    // }
    
    // if (added || updatedNextCourts.length > nextCourts.length) {
    //   setNextCourts(updatedNextCourts);
    //   // No need to manage removedFromNextCourts anymore since all waiting players are eligible
    // }
  };

  // Render a next court
  const renderNextCourt = (court) => {
    // Render team panel for next court
    const renderTeam = (teamNumber, playerIds) => {
      const isTeam1 = teamNumber === 1;
      const bgColor = isTeam1 ? 'bg-team1-light' : 'bg-team2-light';
      const textColor = isTeam1 ? 'text-team1' : 'text-team2';

      const needsPlayer = playerIds.length < playersPerSide;
      const otherTeam = teamNumber === 1 ? court.team2 : court.team1;
      const hasImbalance = playerIds.length !== otherTeam.length;

      const teamClasses = `${bgColor} p-3 rounded-lg ${needsPlayer && hasImbalance ? 'border-2 border-danger bg-danger-light' : ''}`;
      
      return (
        <div className={teamClasses}>
          <div className="flex justify-between items-center mb-2">
            <h4 className={`font-semibold ${textColor}`}>Team {teamNumber}</h4>
          </div>
          <ul className="flex flex-wrap gap-2">
            {playerIds.map((playerId, idx) => {
              const player = playersById[playerId];
              return (
                <NextCourtPlayerPill
                  key={playerId}
                  player={player}
                  team={teamNumber}
                  courtNumber={court.courtNumber}
                  onRemove={handleRemovePlayerFromNextCourt}
                  currentClubId={currentClubId}
                  teammate={playerIds.length === 2 ? playersById[playerIds[1 - idx]] : null}
                  useSinglesElo={playersPerSide === 1}
                  waitMetricSlice={playerWaitMetrics[playerId] || null}
                />
              );
            })}
          </ul>
        </div>
      );
    };

    return (
      <div className="border border-border-medium rounded-lg overflow-hidden bg-surface flex flex-col min-h-[300px]">
        <div className="bg-primary/10 p-2 border-b border-primary/20">
          <h3 className="font-medium">Next Court</h3>
        </div>
        <div className="p-4 flex-grow flex flex-col">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 flex-grow">
            {renderTeam(1, court.team1)}
            {renderTeam(2, court.team2)}
          </div>
        </div>
      </div>
    );
  };

  // Remove all console.log statements related to courtAssignments or rendering courts
  return (
    <>
      {/* Removed duplicate Court Assignments heading; handled by Dashboard.js */}
      
      {/* Active Courts */}
      {courtAssignments.length > 0 ? (
        <div
          className={`grid overflow-visible ${compactGrid ? 'mt-3 gap-2' : 'mt-6 gap-5'} ${
          (courtLayout === 'auto' || courtLayout === '1') ? 'grid-cols-1 max-w-2xl mx-auto' :
          courtLayout === '2' ? 'grid-cols-1 lg:grid-cols-2' :
          courtLayout === '3' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' :
          courtLayout === '4' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4' :
          'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'
        }`}
        >
            {courtAssignments.map((court, index) => (
                  <CourtAssignment
                    key={court.courtNumber}
                    court={court}
                    courtIndex={index}
                    onMoveCourt={isHostView ? onMoveCourt : undefined}
                    courtReorderBusy={courtReorderBusy}
                    playingCourtCount={courtAssignments.length}
                    courtMoveFollowsStack={courtsStackedVertically}
                    highlightFreshRotation={
                      freshRotationHighlightCourtNumber != null &&
                      Number(court.courtNumber) === Number(freshRotationHighlightCourtNumber)
                    }
                    gridDensity={gridDensity}
                    onUpdateResult={updateMatchResult}
                    onRotatePlayers={rotatePlayersAfterMatch}
                    onRemoveCourt={removeCourt}
                    onRemovePlayerFromCourt={onRemovePlayerFromCourt}
                    onSwapPlayer={handleSwapPlayer}
                    onFillEmptyCourt={onFillEmptyCourt}
                    onPromoteUpcomingMatches={onPromoteUpcomingMatches}
                    nextAssignments={nextAssignments}
                    currentClubId={currentClubId}
                    playersById={playersById}
                    isSocialSession={isSocialSession}
                    availablePlayers={pickPlayingPlayers}
                    waitingManagementPlayers={pickWaitingManagementPlayers}
                    sessionPlayerStatus={sessionPlayerStatus}
                    onAddPlayerToCourt={onAddPlayerToCourt}
                    matchmakingValidation={matchmakingValidation}
                    playerStreaks={playerStreaks}
                    canEnterMatchScores={canEnterMatchScores}
                    showPostMatchRotateButton={showPostMatchRotateButton}
                    playersPerSide={playersPerSide}
                    playerWaitMetrics={playerWaitMetrics}
                    courtCardLayout={courtCardLayout}
                    viewerPlayerId={viewerPlayerId}
                    partnerships={partnerships}
                    scoringConfig={scoringConfig}
                    scheduledGames={scheduledGames}
                    showEloTooltip={showEloTooltip}
                    pegBoardQueue={pegBoardQueue}
                    pegBoardPickTarget={pegBoardPickTarget}
                    onOpenPegBoardPick={onOpenPegBoardPick}
                  />
            ))}
          </div>
      ) : (
        <div className="text-text-muted">No court assignments yet.</div>
      )}
      
      {/* Next On Court UI hidden for now */}
      
      {/* Waiting List */}
      {/* (Removed from here; will be rendered in Dashboard.js) */}
    </>
  );
};

export default CourtsList; 